import dayjs from 'dayjs';
import { isAxiosError } from 'axios';
import { useFetchTransactionPostingPeriod } from '../../../hooks/useFetchTransactionPostingPeriod';
import { useEffect, useState } from 'react';
import InformationPopup from '../../informationPopup/informationPopup';
import { useTranslation } from 'react-i18next';
import { useCreateTransactionPostingPeriod } from '../../../hooks/useCreateTransactionPostingPeriod';
import { TransactionPostingPeriodStatus } from '../../../constants/constants';
import { Spin } from '../../Ant';
import { formatter } from '../../../constants/utilityFunctions';
import { IParameters } from '../../../interface/parameters';
import { IAPIError, TransactionPostingPeriodException } from '../../../interface/common';
import { showToast } from '../../utils/toast';

const POSTING_PERIOD_OPEN_ERRORS: Record<string, string> = {
  [TransactionPostingPeriodException.OpeningOutOfSequenceIsNotAllowedException]:
    'transaction:postingPeriod.periodCannotBeOpenedOutOfSequence',
  [TransactionPostingPeriodException.OpeningTooFarIntoTheFutureException]:
    'transaction:postingPeriod.periodInFutureChooseAnotherDate',
};

const TransactionPostingPeriod = ({
  checkTransactionPostingPeriod,
  transactionDate,
  parametersDetails,
  onClose,
  onPeriodUnavailable,
}: {
  checkTransactionPostingPeriod: boolean;
  transactionDate: string;
  parametersDetails?: IParameters;
  onClose: (saveTransactionFlag?: boolean) => void;
  onPeriodUnavailable: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const createTransactionPostingPeriod = useCreateTransactionPostingPeriod();
  const [postingPeriodOptionsDialogVisible, setPostingPeriodOptionsDialogVisible] =
    useState<boolean>(false);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  useEffect(() => {
    if (!checkTransactionPostingPeriod || !parametersDetails) {
      return;
    }

    if (
      !parametersDetails?.parameters?.transactionPostingPeriodParameters
        ?.allowOpeningOfPostingPeriodsInFuture &&
      dayjs().isoWeek() < dayjs(transactionDate).isoWeek()
    ) {
      onPeriodUnavailable(
        t('transaction:postingPeriod.periodInFutureChooseAnotherDate', {
          transactionDate: formatter.dateStandard(new Date(transactionDate)),
        }),
      );
      setShouldFetch(false);
      return;
    }

    setShouldFetch(true);
  }, [checkTransactionPostingPeriod, parametersDetails, transactionDate, t]);
  const { data: postingPeriodResponse, isLoading: isLoadingPostingPeriod } =
    useFetchTransactionPostingPeriod(
      {
        extraFilters: {
          weekStartDate: `$lte:${dayjs(transactionDate).format('YYYY-MM-DD')}`,
          weekEndDate: `$gte:${dayjs(transactionDate).format('YYYY-MM-DD')}`,
        },
      },
      checkTransactionPostingPeriod && shouldFetch,
    );
  const handleOpenPeriodAndSave = () => {
    setPostingPeriodOptionsDialogVisible(false);
    createTransactionPostingPeriod.mutate(
      { data: { date: transactionDate } },
      {
        onSuccess: () => {
          onClose(true);
        },
        onError: (error: unknown) => {
          const errorType = isAxiosError<IAPIError>(error) ? error.response?.data?.type : undefined;
          const translationKey = errorType ? POSTING_PERIOD_OPEN_ERRORS[errorType] : undefined;

          if (translationKey) {
            onPeriodUnavailable(
              t(translationKey, {
                transactionDate: formatter.dateStandard(new Date(transactionDate)),
              }),
            );
          } else {
            showToast('error', t('common:somethingWentWrongTryAgain'));
          }
          onClose();
        },
      },
    );
  };

  useEffect(() => {
    if (postingPeriodResponse !== undefined && postingPeriodResponse.data.length === 0) {
      setPostingPeriodOptionsDialogVisible(true);
      return;
    }

    if (postingPeriodResponse?.data[0]?.status === TransactionPostingPeriodStatus.OPEN) {
      setPostingPeriodOptionsDialogVisible(true);
      onClose(true);
    } else if (postingPeriodResponse?.data[0].status === TransactionPostingPeriodStatus.LOCKED) {
      onPeriodUnavailable(
        t('transaction:postingPeriod.periodIsLockedChooseAnotherDate', {
          transactionDate: formatter.dateStandard(new Date(transactionDate)),
        }),
      );
    }
  }, [postingPeriodResponse]);

  return (
    <div>
      {isLoadingPostingPeriod && <Spin fullscreen data-testid="loading-indicator" />}
      <InformationPopup
        visible={postingPeriodOptionsDialogVisible}
        onClose={() => {
          setPostingPeriodOptionsDialogVisible(false);
          setTimeout(() => {
            onClose();
          }, 500);
        }}
        title={t('transaction:postingPeriod.periodNotOpenDoYouWantToOpenAndSaveTransaction', {
          transactionDate: formatter.dateStandard(new Date(transactionDate)),
        })}
        cancelText={t('common:cancel')}
        confirmText={t('transaction:postingPeriod.openPeriodAndSave')}
        onConfirm={handleOpenPeriodAndSave}
      />
    </div>
  );
};

export default TransactionPostingPeriod;
